import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Building2, ShoppingBag, Briefcase, Store, Handshake, Wallet, Globe2, Plane } from "lucide-react";
import SEO from "../components/SEO";

const ICONS = [Building2, ShoppingBag, Wallet, Briefcase, Store, Handshake, Globe2, Plane];

const UseCasesPage = () => {
  const { t } = useTranslation();
  const useCases = t("useCases.items", { returnObjects: true });
  const diffItems = t("useCases.diff.items", { returnObjects: true });

  return (
    <div data-testid="usecases-page">
      <SEO
        path="/casos-de-uso"
        title={t("seo.useCases.title")}
        description={t("seo.useCases.description")}
        keywords={t("seo.useCases.keywords")}
      />
      {/* Hero */}
      <section className="hero" style={styles.hero}>
        <div className="container">
          <div className="heroInner" style={styles.heroInner}>
            <div className="eyebrow" style={styles.eyebrow}>{t("useCases.hero.eyebrow")}</div>
            <h1 className="heroTitle" style={styles.heroTitle}>
              {t("useCases.hero.titleLine1")}<br />{t("useCases.hero.titleLine2")}
            </h1>
            <p className="heroSubtitle" style={styles.heroSubtitle}>{t("useCases.hero.subtitle")}</p>
          </div>
        </div>
      </section>

      {/* Use cases grid */}
      <section className="gridSection" style={styles.gridSection}>
        <div className="container">
          <div className="grid" style={styles.grid}>
            {useCases.map((uc, i) => {
              const Icon = ICONS[i] || Building2;
              return (
                <div
                  key={i}
                  className="stack-card card"
                  style={styles.card}
                  data-testid={`usecase-card-${i}`}
                >
                  <div className="cardIcon" style={styles.cardIcon}>
                    <Icon size={22} strokeWidth={1.75} />
                  </div>
                  <div className="stack-number cardTag" style={styles.cardTag}>{uc.tag}</div>
                  <h3 className="stack-title cardTitle" style={styles.cardTitle}>{uc.title}</h3>
                  <p className="stack-description cardDesc" style={styles.cardDesc}>{uc.description}</p>
                  <ul className="list" style={styles.list}>
                    {uc.highlights.map((h, j) => (
                      <li key={j} className="stack-description listItem" style={styles.listItem}>
                        <span className="bullet" style={styles.bullet}>—</span>
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Differentiators strip */}
      <section className="diffSection" style={styles.diffSection}>
        <div className="container">
          <div className="diffHeader" style={styles.diffHeader}>
            <h2 className="diffTitle" style={styles.diffTitle}>{t("useCases.diff.titleLine1")}<br />{t("useCases.diff.titleLine2")}</h2>
            <p className="diffSubtitle" style={styles.diffSubtitle}>{t("useCases.diff.subtitle")}</p>
          </div>
          <div className="diffGrid" style={styles.diffGrid}>
            {diffItems.map((d, i) => (
              <div key={i} className="diffItem" style={styles.diffItem}>
                <div className="diffNum" style={styles.diffNum}>{d.number}</div>
                <div className="diffLabel" style={styles.diffLabel}>{d.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ctaSection" style={styles.ctaSection}>
        <div className="container">
          <div className="ctaInner" style={styles.ctaInner}>
            <h2 className="ctaTitle" style={styles.ctaTitle}>{t("useCases.cta.title")}</h2>
            <p className="ctaSubtitle" style={styles.ctaSubtitle}>{t("useCases.cta.subtitle")}</p>
            <Link to="/contacto">
              <button className="btn-primary ctaButton" style={styles.ctaButton} data-testid="usecases-final-cta">
                {t("common.scheduleADemo")}
                <ArrowRight size={18} style={{ marginLeft: 8 }} />
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const styles = {
  hero: {
    padding: "160px 20px 80px",
    background: "#ffffff"
  },
  heroInner: {
    maxWidth: "900px",
    margin: "0 auto"
  },
  eyebrow: {
    fontSize: "13px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#f5576c",
    marginBottom: "20px"
  },
  heroTitle: {
    fontSize: "64px",
    fontWeight: 700,
    lineHeight: 1.05,
    letterSpacing: "-0.03em",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "24px"
  },
  heroSubtitle: {
    fontSize: "20px",
    lineHeight: 1.6,
    color: "#4a5568",
    maxWidth: "720px"
  },
  gridSection: {
    padding: "80px 20px 100px",
    background: "#ffffff"
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "28px",
    maxWidth: "1200px",
    margin: "0 auto"
  },
  card: {
    padding: "40px",
    background: "#ffffff",
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: "16px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.04)"
  },
  cardIcon: {
    width: "44px",
    height: "44px",
    borderRadius: "12px",
    background: "#f1f2f8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#667eea",
    marginBottom: "20px"
  },
  cardTag: {
    fontSize: "12px",
    fontWeight: 700,
    color: "#f5576c",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: "12px"
  },
  cardTitle: {
    fontSize: "26px",
    fontWeight: 700,
    color: "#1a202c",
    letterSpacing: "-0.01em",
    lineHeight: 1.25,
    marginBottom: "14px"
  },
  cardDesc: {
    fontSize: "16px",
    color: "#4a5568",
    lineHeight: 1.65,
    marginBottom: "22px"
  },
  list: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    flexDirection: "column",
    gap: "10px"
  },
  listItem: {
    display: "flex",
    gap: "10px",
    fontSize: "15px",
    lineHeight: 1.55,
    color: "#4a5568"
  },
  bullet: {
    color: "#f5576c",
    fontWeight: 700
  },
  diffSection: {
    padding: "100px 20px",
    background: "#f8f9fc",
    borderTop: "1px solid rgba(0,0,0,0.06)"
  },
  diffHeader: {
    textAlign: "center",
    maxWidth: "720px",
    margin: "0 auto 48px"
  },
  diffTitle: {
    fontSize: "52px",
    fontWeight: 700,
    lineHeight: 1.1,
    background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "14px",
    letterSpacing: "-0.02em"
  },
  diffSubtitle: {
    fontSize: "18px",
    color: "#4a5568"
  },
  diffGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "24px",
    maxWidth: "1100px",
    margin: "0 auto"
  },
  diffItem: {
    padding: "32px",
    background: "#ffffff",
    borderRadius: "16px",
    border: "1px solid rgba(0,0,0,0.08)",
    textAlign: "left"
  },
  diffNum: {
    fontSize: "22px",
    fontWeight: 700,
    color: "#1a202c",
    marginBottom: "8px",
    letterSpacing: "-0.01em"
  },
  diffLabel: {
    fontSize: "15px",
    color: "#4a5568",
    lineHeight: 1.6
  },
  ctaSection: {
    padding: "100px 20px",
    background: "#ffffff",
    borderTop: "1px solid rgba(0,0,0,0.08)"
  },
  ctaInner: {
    maxWidth: "700px",
    margin: "0 auto",
    textAlign: "center"
  },
  ctaTitle: {
    fontSize: "52px",
    fontWeight: 700,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "20px",
    letterSpacing: "-0.02em"
  },
  ctaSubtitle: {
    fontSize: "19px",
    color: "#4a5568",
    lineHeight: 1.6,
    marginBottom: "36px"
  },
  ctaButton: {
    fontSize: "17px",
    padding: "16px 36px"
  }
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @media (max-width: 1024px) {
    [data-testid="usecases-page"] div[style*="grid-template-columns: repeat(2"] { grid-template-columns: 1fr !important; }
    [data-testid="usecases-page"] div[style*="grid-template-columns: repeat(3"] { grid-template-columns: 1fr !important; }
    [data-testid="usecases-page"] h1 { font-size: 44px !important; }
    [data-testid="usecases-page"] h2 { font-size: 38px !important; }
  }
  @media (max-width: 640px) {
    [data-testid="usecases-page"] section:first-child { padding: 120px 16px 60px !important; }
    [data-testid="usecases-page"] h1 { font-size: 34px !important; }
    [data-testid="usecases-page"] h2 { font-size: 30px !important; }
    [data-testid="usecases-page"] div[style*="padding: 40px"] { padding: 26px !important; }
  }
`;
document.head.appendChild(styleSheet);

export default UseCasesPage;
